/**
 * Canonical listing lifecycle status (Active vs Expired).
 *
 * A listing's status is driven ONLY by the platform fields the app owns:
 * `is_active` and `expires_at`. The REGA license end date
 * (`rega_display_data.endDate`) is display-only metadata — it can be stale or a
 * placeholder — so it must NEVER flip a listing to "Expired". Otherwise an
 * active listing with no platform expiry (e.g. one imported from REGA whose
 * license endDate is in the past) wrongly renders as expired even though the
 * dashboard filter tabs and stat counters still treat it as active.
 *
 * Use these helpers everywhere a listing's Active/Expired status is decided —
 * the card badge, the table badge, the detail page, the dashboard filter tabs,
 * and the stat counters — so all surfaces always agree.
 */
export interface ListingLifecycle {
  is_active?: boolean | null;
  expires_at?: string | null;
}

/** True when the listing is inactive OR its `expires_at` has passed. */
export function isListingExpired(listing: ListingLifecycle, now: Date = new Date()): boolean {
  if (!listing.is_active) return true;
  return !!listing.expires_at && new Date(listing.expires_at) <= now;
}

/** True when the listing is active and not past its `expires_at`. */
export function isListingActive(listing: ListingLifecycle, now: Date = new Date()): boolean {
  return !isListingExpired(listing, now);
}
